import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Image,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  Badge,
  Flex,
  Icon,
  HStack,
  Card,
  CardBody,
  CardHeader
} from "@chakra-ui/react";
import { CheckIcon, ChevronRightIcon, CalendarIcon, StarIcon } from "@chakra-ui/icons";
import { FaSeedling, FaHandHoldingUsd, FaBook, FaUsers } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const IntroSession = () => {
  const navigate = useNavigate();
  const bg = useColorModeValue("#EFEDDC", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");
  const highlightColor = useColorModeValue("green.600", "green.300");
  
  const handleRegisterClick = () => {
    window.location.href = "/sessions/practitioner-calendar/67f32cb6e7217642b7a9194d";
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1200px" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box 
            textAlign="center" 
            py={8} 
            px={6} 
            borderRadius="lg" 
            bg="green.50" 
            boxShadow="md"
            position="relative"
            overflow="hidden"
            _before={{
              content: "\"\"",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "5px",
              background: "linear-gradient(90deg, #38A169, #48BB78)",
              zIndex: 0
            }}
          >
            <Badge colorScheme="green" fontSize="md" px={3} py={1} mb={3}>
              Special Invitation
            </Badge>
            <Heading as="h1" size="2xl" mb={4} color="green.700">
              Join Our Regenerative Agriculture Introduction Session
            </Heading>
            <Text fontSize="xl" mb={6} color="gray.600">
              Learn how we're building sustainable financial systems and clubs to deliver the 
              best environment for healthy food for our friends and family
            </Text>
            <HStack justify="center" mb={8}>
              <Button 
                colorScheme="green" 
                size="lg" 
                rightIcon={<CalendarIcon />}
                onClick={handleRegisterClick}
                px={8}
                py={6}
                fontSize="lg"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "xl"
                }}
                transition="all 0.3s ease"
              >
                Register for an Intro Session
              </Button>
            </HStack>
            <Text fontSize="sm" color="gray.500">
              Join us for a 10-minute introduction to the Life Essentials Club and our vision
            </Text>
          </Box>

          {/* What You'll Learn Section */}
          <Box>
            <Heading as="h2" size="xl" mb={6} textAlign="center" color={highlightColor}>
              What You'll Learn in Our 10-Minute Session
            </Heading>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
              <Card bg={cardBg} boxShadow="md" borderRadius="lg" height="100%">
                <CardHeader>
                  <Flex align="center">
                    <Icon as={FaUsers} w={6} h={6} color="blue.500" mr={3} />
                    <Heading size="md">About Our Club</Heading>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <List spacing={3}>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      How we're connecting communities and empowering consumers
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Our vision for transparent and accountable food systems
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      How membership works and the benefits of joining
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Collective support for local farmers and producers
                    </ListItem>
                  </List>
                </CardBody>
              </Card>

              <Card bg={cardBg} boxShadow="md" borderRadius="lg" height="100%">
                <CardHeader>
                  <Flex align="center">
                    <Icon as={FaHandHoldingUsd} w={6} h={6} color="purple.500" mr={3} />
                    <Heading size="md">Financial Systems</Heading>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <List spacing={3}>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      How we're creating better economics for essential foods
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Group-buying power for better pricing and access
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Funding regenerative agriculture initiatives
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Creating direct producer-to-consumer relationships
                    </ListItem>
                  </List>
                </CardBody>
              </Card>
            </SimpleGrid>
          </Box>

          {/* Book Section */}
          <Box 
            py={10} 
            px={8} 
            bg="green.50" 
            borderRadius="lg" 
            boxShadow="md"
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
              <Box>
                <Flex align="center" mb={4}>
                  <Icon as={FaBook} w={6} h={6} color="green.500" mr={3} />
                  <Heading as="h2" size="xl" color={highlightColor}>
                    Our Regenerative Guide
                  </Heading>
                </Flex>
                <Heading as="h3" size="lg" mb={4}>
                  "Regenerative Agriculture: A Manual for Making Small Farms Work"
                </Heading>
                <Text fontSize="md" mb={3}>
                  by Richard Perkins
                </Text>
                <Text mb={4}>
                  This comprehensive guide forms the foundation of our approach to funding and supporting 
                  regenerative agriculture initiatives. It offers clear and pragmatic approaches to designing, 
                  installing, and managing profitable small farms.
                </Text>
                <List spacing={3} mb={6}>
                  <ListItem>
                    <ListIcon as={CheckIcon} color="green.500" />
                    Detailed strategies for creating profitable regenerative farms
                  </ListItem>
                  <ListItem>
                    <ListIcon as={CheckIcon} color="green.500" />
                    Holistic decision-making frameworks for farm design
                  </ListItem>
                  <ListItem>
                    <ListIcon as={CheckIcon} color="green.500" />
                    Comparative analysis of 13 farm enterprises
                  </ListItem>
                  <ListItem>
                    <ListIcon as={CheckIcon} color="green.500" />
                    Financial planning for sustainable agriculture
                  </ListItem>
                </List>
              </Box>
              <Box textAlign="center">
                <Box 
                  bg="white" 
                  p={3} 
                  borderRadius="md" 
                  boxShadow="lg" 
                  maxW="300px" 
                  mx="auto"
                  transform="rotate(3deg)"
                  _hover={{
                    transform: "rotate(0deg) scale(1.03)",
                    boxShadow: "xl"
                  }}
                  transition="all 0.3s ease"
                >
                  <Image 
                    src="/book.jpg" 
                    alt="Regenerative Agriculture: A Manual for Making Small Farms Work" 
                    borderRadius="sm"
                  />
                </Box>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Key Points Section */}
          <Box>
            <Heading as="h2" size="xl" mb={6} textAlign="center" color={highlightColor}>
              Key Points About Regenerative Funding
            </Heading>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <Card bg={cardBg} boxShadow="md" borderRadius="lg" height="100%">
                <CardHeader bg="blue.50" borderTopRadius="lg">
                  <Flex align="center">
                    <Icon as={FaSeedling} w={5} h={5} color="blue.500" mr={2} />
                    <Heading size="sm">Profitable Farm Setups</Heading>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Text>
                    The book provides comprehensive guidance on making regenerative farms profitable 
                    from the start, with strategies proven to work on small scales from 1-1000 hectares.
                  </Text>
                </CardBody>
              </Card>

              <Card bg={cardBg} boxShadow="md" borderRadius="lg" height="100%">
                <CardHeader bg="green.50" borderTopRadius="lg">
                  <Flex align="center">
                    <Icon as={FaHandHoldingUsd} w={5} h={5} color="green.500" mr={2} />
                    <Heading size="sm">Funding Sources</Heading>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Text>
                    Learn how to generate farm income that can be reinvested into further regenerative 
                    efforts, plus insights into accessing grants and other funding opportunities.
                  </Text>
                </CardBody>
              </Card>

              <Card bg={cardBg} boxShadow="md" borderRadius="lg" height="100%">
                <CardHeader bg="purple.50" borderTopRadius="lg">
                  <Flex align="center">
                    <Icon as={StarIcon} w={5} h={5} color="purple.500" mr={2} />
                    <Heading size="sm">Real Success Stories</Heading>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Text>
                    Based on Perkins' own farm that grosses €250,000 in a 6-month season and was 
                    profitable from year one, showing the economic viability of regenerative practices.
                  </Text>
                </CardBody>
              </Card>
            </SimpleGrid>
          </Box>

          {/* Call to Action */}
          <Box 
            textAlign="center" 
            py={10} 
            px={6} 
            bg="blue.50" 
            borderRadius="lg" 
            boxShadow="md"
          >
            <Heading as="h2" size="xl" mb={4} color="blue.700">
              Ready to Learn More?
            </Heading>
            <Text fontSize="lg" mb={8} maxW="700px" mx="auto">
              Join us for a special 10-minute introduction session where we'll share our vision, explain how 
              our club works, and show you how you can be part of this regenerative movement.
            </Text>
            <Button 
              colorScheme="blue" 
              size="lg" 
              rightIcon={<ChevronRightIcon />}
              onClick={handleRegisterClick}
              px={10}
              py={7}
              fontSize="xl"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "xl"
              }}
              transition="all 0.3s ease"
            >
              Book a Call with Tom to Discuss
            </Button>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default IntroSession; 